import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { injectIntl } from 'react-intl';
import { ICollectionPoint, ICollectionPointOperator, ICollectionPointUpdate } from '../../util/DataModelInterface';
import { Form, InputGroup, ListGroup } from 'react-bootstrap';
import { getCollectionpointOperators } from '../../util/StorageUtil';
import { addNewCollectionpoint, updateCollectionpointOperator, addCollectionpointOperator, emptyPoint } from '../../api/CollectionpointOperators';
import { AlertModal, ConfirmModal } from '../../components/ReportModals';

export interface IOperatorProps {
  intl: any;
  addNew: boolean;
  operator: ICollectionPointOperator;
  addPoint: ICollectionPointUpdate;
  updatePoint: ICollectionPointUpdate;
  editPoint: (p: ICollectionPoint) => void;
  update: () => void;
  modal: () => void;
  error: (msg: string) => void;
  close: () => void;
  pointAdded: () => void;
}

export interface IOperatorState {
  showDetails: boolean;
  name: string;
  emails: string[];
  autoSend: boolean;
  reportFormat: string;
  points: ICollectionPointUpdate[];
  newEmail: "";
  addEmail: boolean;
  alertSave: boolean;
  confirmClose: boolean;
}

class Operator extends React.Component<IOperatorProps, IOperatorState> {
  constructor(props: any) {
    super(props);
    this.state = {
      showDetails: false,
      name: props.operator.name,
      emails: props.operator.emails,
      autoSend: props.operator.autoemail,
      reportFormat: props.operator.reportformat,
      points: [] as ICollectionPointUpdate[],
      newEmail: "",
      addEmail: false,
      alertSave: false,
      confirmClose: false
    }
  }

  shouldComponentUpdate(newProps: IOperatorProps, newState: IOperatorState) {
    if (newProps.addNew !== this.props.addNew ||
        newProps.operator !== this.props.operator ||
        newProps.addPoint !== this.props.addPoint ||
        newProps.updatePoint !== this.props.updatePoint ||
        newState !== this.state) {
      return true;
    } else {
      return false;
    }
  }

  static getDerivedStateFromProps(props: IOperatorProps, state: IOperatorState) {
    if(props.addPoint.info.name !== "") {
      if(!state.points.some( p => p === props.addPoint )) {
        state.points.push(props.addPoint);
        props.pointAdded();
      }
      return state;
    }

    if(props.updatePoint.info.name !== "") {
      let index = props.operator.collection_points.findIndex( p => p.info.collectionpoint_id === props.updatePoint.info.collectionpoint_id);
      if(index > -1) {
        props.operator.collection_points[index].info = props.updatePoint.info;
      }
      return state;
    }
    return null;
  }

  edited = () : boolean => {
    let edited = false;
    // Allow saving only when data has been edited
    if(JSON.stringify(this.state.emails) !== JSON.stringify(this.props.operator.emails) || // email added or removed OR
       this.state.autoSend !== this.props.operator.autoemail ||                            // autosend option updated OR
       (this.state.name !== this.props.operator.name && this.state.name !== "") ||         // name updated AND not empty OR
       (this.state.reportFormat !== this.props.operator.reportformat) ||                   // reportformat changed
       (this.state.points.length !== 0)) {                                                 // collection point(s) added
      edited = true;
    }
    return edited;
  };

  missing = () : boolean => {
    let missing = false;
    // Do not allow saving if name is empty or there are no collection points (mandatory fields)
    if(this.state.name === "" || (this.state.points.length === 0 && this.props.operator.collection_points.length === 0)) {
      missing = true;
    }
    return missing;
  };

  resetState = () => {
    this.setState({name: this.props.operator.name, 
                   autoSend: this.props.operator.autoemail,
                   emails: this.props.operator.emails,
                   reportFormat: this.props.operator.reportformat,
                   points: [] as ICollectionPointUpdate[],
                   newEmail: "",
                   addEmail: false,
                   showDetails: false});
  };

  addOperator = () => {
    addCollectionpointOperator(
            { id: "",
              name: this.state.name,
              collection_points: this.state.points,
              autoemail: this.state.autoSend,
              reportformat: this.state.reportFormat,
              emails: this.state.emails
            }).then((response) => {
      if(response === false || response.error) {
        this.props.error(response.error);
      } else {
        this.props.update();
      }
    }).finally(() => {
      this.resetState();
      this.props.close();
    });
  }

  sendUpdates = () => {
    var updates = [] as Promise<any>[];
    updates.push(updateCollectionpointOperator({ id: this.props.operator.id,
                                                  name: this.state.name,
                                                  collection_points: this.props.operator.collection_points,
                                                  autoemail: this.state.autoSend,
                                                  reportformat: this.state.reportFormat,
                                                  emails: this.state.emails }));

    if(this.state.points.length!==0) {
      this.state.points.forEach((point) => {
        point.info.operator_id = this.props.operator.id;
        updates.push(addNewCollectionpoint(point));
      });
    }

    Promise.all(updates).then((response) => {
      let msg = null;
      response.forEach((r) => {
        if(r === false) msg = '';
        else if(r.error) msg = r.error; 
      });
      if(msg !== null) {
        this.props.error(msg);
      } else {
        this.props.update();
      }
    }).finally(() => {
      this.setState({ points: [] as ICollectionPointUpdate[],
                      showDetails: true});
    });
  }

  parseCollectionPointNames = () : string => {
    let names = this.props.intl.formatMessage({id: 'settings.operators.collectionpoints.empty'});
    this.props.operator.collection_points.forEach((point, count) => {
      if(count === 0) names = "";
      if(point.info)
        names += point.info.name;
      if(count < this.props.operator.collection_points.length-1) names += ", ";
    });
    return names;
  }

  canSave = () => {
    let save = true;
    if(this.props.operator.id !== '') return save;
    let operators = getCollectionpointOperators();
    if(operators) {
      let op = operators.find(op => op.name.toLowerCase() === this.state.name.toLowerCase());
      if(op) {
        save = false;
      }
    }
    return save;
  }

  getListItemStyle = (p: ICollectionPoint) : string => {
    let style =  "ListItem d-flex flex-row align-items-center";
    if(p.info.operational_state !== 'InUse') {
      style = "ListItemNotInUse d-flex flex-row align-items-center"
    }
    return style;
  }

  getCollectionPoints = (points: ICollectionPoint[]) => {
    const options: { }[] = [];
    if (points) {
      points.sort(function(a, b){ // Alphabetical order
        if(a.info.name < b.info.name) { return -1; }
        if(a.info.name > b.info.name) { return 1; }
        return 0;
      });
      points.forEach((p : ICollectionPoint) => {
        options.push(
          <ListGroup.Item key={p.info.collectionpoint_id}
                          className={ this.getListItemStyle(p) }
                          onClick={() => { this.props.editPoint(p); }}>
              { p.info ? p.info.name : '' }
              <FontAwesomeIcon className="RightIcon"
                               icon="chevron-right"
                               color="var(--serty-green)"/>
          </ListGroup.Item>
        );
      });
      return options;
    }
  }

  getAddedPoints = (points: ICollectionPointUpdate[]) => {
    const added: { }[] = [];
    if (points) {
      points.forEach((p : ICollectionPointUpdate, index) => {
        added.push(
          <ListGroup.Item key={p.info.collectionpoint_id + index}
                          className={"ListItem d-flex flex-row align-items-center"}
                          onClick={() => { this.setState({points: this.state.points.filter(point => p !== point)}); }}>
              { p.info ? p.info.name : '' }
              <FontAwesomeIcon className="RightIcon"
                               icon={ "times-circle" }
                               color={ "var(--serty-grey)" }/>
          </ListGroup.Item>
        );
      });
      return added;
    }
  }

  showCollectionPointList = () => {
    return (
      <div>
        <div className="d-flex flex-row align-items-center">
          <h3 className={"sub-label" + (this.props.operator.collection_points.length===0 && this.state.points.length === 0 ? " mandatory" : "")}>
            { this.props.intl.formatMessage({id: 'settings.operator.collectionpoints'}) }
          </h3>
          <button
            className={ "ListItemButton btn btn-link ml-auto" } key={this.props.operator.collection_points.length}
            onClick={() => { this.props.editPoint(emptyPoint(this.state.reportFormat==='STORE')) }}>
            <FontAwesomeIcon className={ "LeftIcon" } icon={ "plus-circle" } color="var(--serty-green)"/>
            { this.state.reportFormat==='STORE' ? 
              this.props.intl.formatMessage({id: 'settings.operators.addstorepoint'}) : 
              this.props.intl.formatMessage({id: 'settings.operators.addcollectionpoint'}) }
          </button>
        </div>
        <ListGroup>
          { this.getCollectionPoints(this.props.operator.collection_points) }
          { this.getAddedPoints(this.state.points) }
        </ListGroup>
      </div>
    );
  }

  createEmailElement = (email: string) => {
    if(email)
      return(
      <ListGroup.Item className="ListItemButton d-flex email-item align-items-center" key={email}>
        <span>{ email }</span>
        <FontAwesomeIcon className="RightIcon" icon={ "times-circle" } color={"var(--serty-grey)"} onClick={() => { this.removeEmail(email) }}/>
      </ListGroup.Item>
      );
    else return <></>;
  }

  removeEmail = (email: string) => {
    this.setState({emails: this.state.emails.filter(e => e !== email)});
  }

  showEmails = () => {
    return (
      <div>
        <div className="d-flex flex-row align-items-center">
          <h3 className="sub-label">{ this.props.intl.formatMessage({id: 'settings.operators.emails'}) }</h3>
          <button
            className={ "ListItemButton btn btn-link ml-auto" } key={this.state.emails ? this.state.emails.length : 0}
                            onClick={() => { this.setState({addEmail: !this.state.addEmail}) }}>
            <FontAwesomeIcon className="LeftIcon" icon={ "plus-circle" } color="var(--serty-green)"/>
            { this.props.intl.formatMessage({id: 'settings.operators.addemail'}) }
          </button>
        </div>

        <ListGroup>
          { this.state.emails.map(this.createEmailElement) }
          { this.addEmailView() }
        </ListGroup>
      </div>
    );
  }

  addEmailView = () => {
    // Simple email check:
    // Contains only one @, a period somewhere after it,
    // at least one character before the @, before the period and after it,
    // does not allow < > or space.
    // eslint-disable-next-line no-useless-escape
    let emailFormat = new RegExp('^[^@<> ]+@[^@<> ]+\.[^@<> ]+$');
    return(
      <div className={ !this.state.addEmail ? 'hidden' : 'add-view add-view-email' }>
        <InputGroup>
            <Form.Group controlId="newemail">
              <Form.Label>{ this.props.intl.formatMessage({ id: 'settings.collectionpoints.emails.add' }) }</Form.Label>
              <Form.Control className="card-text" size="sm" type="text" value={this.state.newEmail}
                            placeholder={ this.props.intl.formatMessage({id: 'settings.collectionpoints.emails.add'}) }
                            onChange={(e: any) => { this.setState({newEmail: e.target.value}); }}/>
            </Form.Group>
        </InputGroup>
        <div className="action-buttons">
          <button className="tiny-ok-button btn-primary btn" disabled={ this.state.newEmail.search(emailFormat) === -1 }
                  onClick={() => {
                    var newEmails = [] as string[];
                    newEmails.push(this.state.newEmail.replace(/\s/g, "")); // remove whitespace
                    this.setState({ emails: this.state.emails.concat(newEmails),
                                    addEmail: !this.state.addEmail,
                                    newEmail: ""});
                  }} >
            {this.props.intl.formatMessage({id: 'settings.collectionpoints.crossreferences.add.ok'})}
          </button>
        </div>
      </div>);
  }

  operatorHeader = () => {
    let newstyle = (this.props.addNew ? 'new-content' : '' );
    return(
      <div className={ this.state.showDetails || this.props.addNew ? 'card-content-wrp collapsed' : ('card-content-wrp ' + newstyle)}>
        {!this.props.addNew && 
        <div className="card-header d-flex flex-row" >
            <div className="card-subtitle id">{this.props.operator.id}</div>
            <div className="card-subtitle name">{this.state.name}</div>
            {<div className={ this.state.showDetails ? 'hidden' : 'card-subtitle details' }> { this.parseCollectionPointNames() } </div>}
            <button className="btn btn-link ml-md-auto list-button" onClick={async () => {
                if(!this.state.showDetails) {
                  this.setState({showDetails: true});
                } else if (this.state.points.length !== 0 || this.edited()) {
                  this.setState( { confirmClose: true } );
                } else {
                  this.resetState();
                }
              }}>
              <FontAwesomeIcon icon={ !this.state.showDetails ? "chevron-down" : "chevron-up" }/>
            </button>
        </div>}

        <div className={ this.state.showDetails || this.props.addNew ? ('card-body visible ' + newstyle) : 'hidden' }>
          <Form.Group controlId="orgName">
            <Form.Label>{ this.props.intl.formatMessage({id: 'settings.operator.add.placeholder.name'}) }</Form.Label>
            <Form.Control type="text" value={this.state.name}
                    onChange={(e: any) => { this.setState({name: e.target.value}); }}/>
          </Form.Group>
          <Form.Group>
          <Form.Check className={ this.state.reportFormat==='STORE' ? 'hidden' : 'visible' } type="checkbox">
            <Form.Check.Input type="checkbox" isValid checked={this.state.reportFormat === 'LAPLAND'} 
                              onChange={(e: any) => { this.setState({reportFormat: e.target.checked ? 'LAPLAND' : 'SERTY'})}}/>
              <Form.Check.Label>{this.props.intl.formatMessage({id: 'settings.operators.reportformat'})}</Form.Check.Label>
            </Form.Check>
          </Form.Group>

          {this.showCollectionPointList()}
          {this.showEmails()}

          <Form.Group controlId="sendingMail">
            <Form.Check type="checkbox">
              <Form.Check.Input type="checkbox" isValid checked={this.state.autoSend} onChange={(e: any) => { this.setState({autoSend: e.target.checked}) }} />
              <Form.Check.Label>{this.props.intl.formatMessage({id: 'settings.operators.autoemail'})}</Form.Check.Label>
            </Form.Check>
          </Form.Group>
        </div>

    </div>);
  }

  render() {
    return (
      <div>
        <AlertModal
          modalOpen={this.state.alertSave}
          messageId={'settings.collectionpoint.operator.exists'}
          onDismiss={() => { this.setState({ alertSave: false }); }} />
        <ConfirmModal
          modalOpen={this.state.confirmClose}
          labelId={ this.state.reportFormat==='STORE' ? 'settings.operators.stores' : 'settings.operators' }
          messageId={'settings.collectionpoint.collectionpoint.close'}
          submitId={'settings.collectionpoints.close'} dismissId={'settings.collectionpoints.cancel'}
          onDismiss={() => { this.setState({ confirmClose: false });  }}
          onSubmit={() => { this.setState({ confirmClose: false }); this.resetState(); if(this.props.operator.id === "") this.props.close(); }} />
        <div className={ 'card' } key={"id-" + this.props.operator.id}>
          {this.operatorHeader()}
          <div className={ (this.props.operator.id === "" || this.state.showDetails) ? 'card-buttons d-flex flex-row p-4' : 'hidden' }>
            <button
                className="btn btn-secondary small-cancel-button ml-auto"
                onClick={async () => {
                  if (this.state.points.length !== 0 || this.edited()) {
                    this.setState( { confirmClose: true } );
                  } else {
                    this.resetState();
                    if(this.props.operator.id === "") this.props.close();
                  }
                }}
                >
                {this.props.intl.formatMessage({id: 'settings.collectionpoints.close'})}
              </button>
              <button disabled={!this.edited() || this.missing()}
                className="btn btn-primary small-save-button"
                onClick={async () => {
                  if(this.canSave()) {
                    this.props.modal();
                    if(this.props.operator.id === "") {
                      this.addOperator();
                    } else {
                      this.sendUpdates();
                    }
                  } else {
                    this.setState( { alertSave: true } );
                  }
                }}
                >
                {this.props.intl.formatMessage({id: 'settings.collectionpoints.save'})}
              </button>
          </div>
        </div>
      </div>
    );
  }
};

export default injectIntl(Operator);
